// Position slot nodes radially around the center and assign handle IDs for edges

function getRootFontSizePx () {
  if (typeof window === 'undefined') return 16
  try {
    const val = parseFloat(window.getComputedStyle(document.documentElement).fontSize)
    return Number.isFinite(val) ? val : 16
  } catch (e) {
    return 16
  }
}

function getCenterHandleIdForAngle (angleRadians, nodeType, orientation = 'pointy') {
  const twoPi = Math.PI * 2
  let a = angleRadians % twoPi
  if (a < 0) a += twoPi
  const deg = a * 180 / Math.PI

  // Handle should point in the direction of the angle
  if (nodeType === 'hexagon') {
    if (orientation === 'flat') {
      // Flat-top hexagon: points at 0°, 60°, 120°, 180°, 240°, 300°
      if (deg >= 330 || deg < 30) return 'handle_right' // 0° = right point
      if (deg >= 30 && deg < 60) return 'handle_rightBottom' // 30-60° = right-bottom point
      if (deg >= 60 && deg < 120) return 'handle_bottom' // 90° = bottom edge
      if (deg >= 120 && deg < 150) return 'handle_leftBottom' // 120-150° = left-bottom point
      if (deg >= 150 && deg < 210) return 'handle_left' // 180° = left point
      if (deg >= 210 && deg < 240) return 'handle_leftTop' // 210-240° = left-top point
      if (deg >= 240 && deg < 300) return 'handle_top' // 270° = top edge
      return 'handle_rightTop' // 300-330° = right-top point
    }

    // Pointy-top hexagon (original)
    if (deg >= 337.5 || deg < 22.5) return 'handle_right' // 0° = right
    if (deg >= 22.5 && deg < 67.5) return 'handle_bottom' // 45° = prefer bottom for 60°
    if (deg >= 67.5 && deg < 112.5) return 'handle_bottom' // 90° = bottom
    if (deg >= 112.5 && deg < 157.5) return 'handle_bottom' // 135° = prefer bottom for 120°
    if (deg >= 157.5 && deg < 202.5) return 'handle_left' // 180° = left
    if (deg >= 202.5 && deg < 247.5) return 'handle_top' // 225° = prefer top for 240°
    if (deg >= 247.5 && deg < 292.5) return 'handle_top' // 270° = top
    return 'handle_top' // 315° = prefer top for 300°
  }

  if (deg >= 0 && deg < 60) return 'handle_rightBottom'
  if (deg >= 60 && deg < 120) return 'handle_bottom'
  if (deg >= 120 && deg < 180) return 'handle_leftBottom'
  if (deg >= 180 && deg < 240) return 'handle_leftTop'
  if (deg >= 240 && deg < 300) return 'handle_top'
  return 'handle_rightTop'
}

function getSlotTargetHandleForAngle (angleRadians, isHexagon, orientation = 'pointy') {
  const twoPi = Math.PI * 2
  let a = angleRadians % twoPi
  if (a < 0) a += twoPi
  const deg = a * 180 / Math.PI

  // Target handles should point back toward center (opposite direction of angle)
  if (isHexagon) {
    if (orientation === 'flat') {
      // Flat-top hexagon: opposite handles for receiving connections
      if (deg >= 330 || deg < 30) return 'target_left' // 0° (right) -> left receives
      if (deg >= 30 && deg < 60) return 'target_leftTop' // 30-60° (right-bottom) -> left-top receives
      if (deg >= 60 && deg < 120) return 'target_top' // 90° (bottom) -> top receives
      if (deg >= 120 && deg < 150) return 'target_rightTop' // 120-150° (left-bottom) -> right-top receives
      if (deg >= 150 && deg < 210) return 'target_right' // 180° (left) -> right receives
      if (deg >= 210 && deg < 240) return 'target_rightBottom' // 210-240° (left-top) -> right-bottom receives
      if (deg >= 240 && deg < 300) return 'target_bottom' // 270° (top) -> bottom receives
      return 'target_leftBottom' // 300-330° (right-top) -> left-bottom receives
    }

    // Pointy-top hexagon (original)
    if (deg >= 337.5 || deg < 22.5) return 'target_left' // 0° (right) -> left receives
    if (deg >= 22.5 && deg < 67.5) return 'target_top' // 45° = prefer top for 60°
    if (deg >= 67.5 && deg < 112.5) return 'target_top' // 90° (bottom) -> top receives
    if (deg >= 112.5 && deg < 157.5) return 'target_top' // 135° = prefer top for 120°
    if (deg >= 157.5 && deg < 202.5) return 'target_right' // 180° (left) -> right receives
    if (deg >= 202.5 && deg < 247.5) return 'target_bottom' // 225° = prefer bottom for 240°
    if (deg >= 247.5 && deg < 292.5) return 'target_bottom' // 270° (top) -> bottom receives
    return 'target_bottom' // 315° = prefer bottom for 300°
  }

  // Original hexagon 6-point layout - targets point back toward center
  if (deg >= 0 && deg < 60) return 'target_leftTop'
  if (deg >= 60 && deg < 120) return 'target_top'
  if (deg >= 120 && deg < 180) return 'target_rightTop'
  if (deg >= 180 && deg < 240) return 'target_rightBottom'
  if (deg >= 240 && deg < 300) return 'target_bottom'
  return 'target_leftBottom'
}

export function layoutLifeMap ({ nodes, edges, centerId, config }) {
  const lifeMapConfig = config?.lifeMap || config || {}
  const radius = lifeMapConfig?.radius ?? 250
  const ringCount = lifeMapConfig?.ringCount ?? 1
  const hexagonOrientation = lifeMapConfig?.hexagonOrientation || 'pointy'
  const center = nodes.find(n => n.id === centerId)
  if (!center) return { nodes, edges }

  center.position = { x: 0, y: 0 }

  const centerIsHexagon = center.type === 'hexagon'

  const centerSizeKey = center?.data?.nodeProps?.size || 'md'
  const hexagonSizeMap = { lg: 180, md: 120, sm: 90 }
  const centerDim = hexagonSizeMap[centerSizeKey] ?? hexagonSizeMap.md
  const originOffset = { x: centerDim / 2, y: centerDim / 2 }

  const slotNodes = nodes.filter(n => n.data.isSlot)
  if (slotNodes.length === 0) return { nodes, edges }

  // set up the slots per ring as we get further out we have more room to spread our wings/rings
  const slotsPerRingArray = []
  let totalAllocated = 0

  for (let ring = 1; ring <= ringCount; ring++) {
    const capacity = 6 * ring
    if (ring === ringCount) {
      // Last ring gets all remaining slots
      slotsPerRingArray.push(slotNodes.length - totalAllocated)
    } else {
      // Earlier rings get their capacity or remaining slots, whichever is smaller
      const allocated = Math.min(capacity, slotNodes.length - totalAllocated)
      slotsPerRingArray.push(allocated)
      totalAllocated += allocated
    }
  }

  let index = 0
  for (let ring = 1; ring <= ringCount; ring++) {
    // Adjust radius multiplier based on orientation
    let radiusMultiplier = 1.0
    if (centerIsHexagon) {
      radiusMultiplier = hexagonOrientation === 'flat' ? 1.45 : 1.4
    }
    const ringRadius = ring * radius * radiusMultiplier
    const count = slotsPerRingArray[ring - 1]

    // For hexagon layouts, calculate angle offset for proper tessellation
    // Outer rings should be rotated to nestle into gaps of inner rings
    let angleOffset = 0
    if (centerIsHexagon && ring > 1) {
      // Rotate outer rings by 30 degrees to align with hexagon gaps
      angleOffset = Math.PI / 6 // 30 degrees
    }

    for (let j = 0; j < count; j++) {
      const node = slotNodes[index]

      // For flat-top hexagons, use specific angles: top, bottom, 2 left, 2 right
      let angle
      if (hexagonOrientation === 'flat' && count === 6) {
        // Custom angles for flat-top: 90° (top), 270° (bottom), 150°, 210° (left), 30°, 330° (right)
        const flatAngles = [
          90 * Math.PI / 180, // j=0: Top center
          30 * Math.PI / 180, // j=1: Top-right
          330 * Math.PI / 180, // j=2: Bottom-right
          270 * Math.PI / 180, // j=3: Bottom center
          210 * Math.PI / 180, // j=4: Bottom-left
          150 * Math.PI / 180 // j=5: Top-left
        ]
        angle = flatAngles[j] || (2 * Math.PI * j) / count + angleOffset
      } else {
        angle = (2 * Math.PI * j) / count + angleOffset
      }

      const size = node?.data?.nodeProps?.size || 'md'
      const nodeIsHexagon = node.type === 'hexagon'

      let w, h
      if (nodeIsHexagon) {
        const hexDim = hexagonSizeMap[size] ?? hexagonSizeMap.md
        // Adjust dimensions based on orientation
        if (hexagonOrientation === 'flat') {
          w = hexDim
          h = hexDim
        } else {
          // Pointy-top hexagons are square aspect ratio in our implementation
          w = hexDim
          h = hexDim
        }
      } else {
        const basePx = getRootFontSizePx()
        const widthEmMap = { lg: 25, md: 20, sm: 16 }
        const fontScaleMap = { lg: 1.25, md: 1, sm: 0.75 }
        const contentWidthPx = (widthEmMap[size] ?? widthEmMap.md) * basePx
        const horizontalPaddingPx = 32 // 16px left + 16px right
        const borderPx = 2 // 1px left + 1px right
        w = contentWidthPx + horizontalPaddingPx + borderPx
        const verticalPaddingPx = 20 // 10px top + 10px bottom
        h = (basePx * (fontScaleMap[size] ?? 1)) + verticalPaddingPx + 2 // +2px border (top+bottom)
      }

      let topAdjustedRadius = ringRadius
      let leftAdjustedRadius = ringRadius
      if (centerIsHexagon && nodeIsHexagon) {
        const deg = (angle * 180 / Math.PI) % 360

        if (hexagonOrientation === 'flat') {
          // Flat-top hexagon: simpler adjustments for even spacing
          // For flat-top hexagons, we just need uniform spacing
          // No adjustments needed - let the base radius multiplier handle it
          topAdjustedRadius = ringRadius
          leftAdjustedRadius = ringRadius
        } else {
          // Pointy-top hexagon adjustments (original logic)
          if ((deg >= 67.5 && deg < 112.5) || (deg >= 247.5 && deg < 292.5)) {
            topAdjustedRadius = ringRadius * 1.22
            leftAdjustedRadius = ringRadius * 1.22
          } else if (
            (deg >= 40 && deg < 67.5) ||
            (deg >= 112.5 && deg < 140) ||
            (deg >= 220 && deg < 247.5) ||
            (deg >= 292.5 && deg < 320)
          ) {
            topAdjustedRadius = ringRadius * 0.90
            leftAdjustedRadius = ringRadius
          }
          if (ring > 1) {
            if (deg === 90 || deg === 270) {
              topAdjustedRadius = ringRadius * 0.78
              leftAdjustedRadius = ringRadius
            } else if (
              (deg > 0 && deg <= 30) ||
              (deg > 145 && deg < 180) ||
              (deg > 180 && deg < 210) ||
              (deg > 325 && deg <= 359)
            ) {
              topAdjustedRadius = ringRadius * 0.78
              leftAdjustedRadius = ringRadius * 0.87
            }
          }
        }
      }

      node.position = {
        x: Math.cos(angle) * leftAdjustedRadius - (w / 2) + originOffset.x,
        y: Math.sin(angle) * topAdjustedRadius - (h / 2) + originOffset.y
      }

      const edge = edges?.find(e => (e.source === centerId && e.target === node.id) || (e.target === centerId && e.source === node.id))
      if (edge) {
        const sourceIsCenter = edge.source === centerId
        const useCardinalHandles = centerIsHexagon || nodeIsHexagon
        const nodeTypeForHandles = useCardinalHandles ? 'hexagon' : null
        const centerHandle = getCenterHandleIdForAngle(angle, nodeTypeForHandles, hexagonOrientation)
        const slotHandle = getSlotTargetHandleForAngle(angle, useCardinalHandles, hexagonOrientation)
        if (sourceIsCenter) {
          edge.sourceHandle = centerHandle
          edge.targetHandle = slotHandle
        } else {
          edge.targetHandle = centerHandle
          edge.sourceHandle = slotHandle
        }
      }
      index++
    }
  }

  return { nodes, edges }
}
